import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { LoadingState, ErrorState, PageHeader } from "../../components/UI";
import { Sidebar } from "../../components/Sidebar";
import SimpleActionFormV2 from "../../components/GreenMiles/CreateAction/SimpleActionForm";
import { getActionTypeInfo } from "../../components/GreenMiles/CreateAction/utils/actionTypeUtils";

const API_URL = process.env.REACT_APP_API_URL || "/api";

function CreateActionPageV2() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [actionType, setActionType] = useState(null);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hotelId && token) {
      fetchHotelInfo();
    }
  }, [hotelId, token]);

  const fetchHotelInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success === "OK") {
        setHotelInfo(data.result);
      } else {
        setError(data.message || "Erreur lors du chargement de l'hôtel");
      }
    } catch (err) {
      console.error("Erreur lors du chargement:", err);
      setError("Erreur lors du chargement de l'hôtel");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    navigate(`/hotels/${hotelId}/greenmiles/actions`, {
      state: {
        message: "Action créée avec succès !",
        type: "success",
      },
    });
  };

  const handleCancel = () => {
    navigate(`/hotels/${hotelId}/greenmiles/actions`);
  };

  if (loading) {
    return <LoadingState message="Chargement des données..." />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  }

  // Si aucun type n'est sélectionné, afficher le sélecteur de type
  if (!actionType) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 overflow-auto">
          <PageHeader title={`Créer une action - ${hotelInfo?.hName || hotelId}`} onGoBack={handleCancel} />
          <div className="p-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Choisir le type d'action</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {["LUNICLEAN", "SKIP_SERVICE", "LUNISHOWER", "YESNO", "YESNOSTAY", "CUSTOM", "REVIEW", "LIMEAT", "CLAIM", "PROOF", "YESNOMAIL", "QUIZ", "LINK"].map((type) => {
                  const info = getActionTypeInfo(type);
                  return (
                    <button key={type} onClick={() => setActionType(type)} className="p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left">
                      <div className="font-medium text-gray-900 mb-1">{info?.displayName || type}</div>
                      <div className="text-sm text-gray-600">{info?.description || ""}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Afficher le formulaire une fois le type sélectionné
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <PageHeader title={`Créer une action - ${hotelInfo?.hName || hotelId}`} onGoBack={() => setActionType(null)} />
        <div className="p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-6">
            <SimpleActionFormV2 hotelId={hotelId} actionType={actionType} onSuccess={handleSuccess} onCancel={handleCancel} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateActionPageV2;
